import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Globe2, Building2, Cpu, Play, Sparkles, Clock, Mic2, FileText } from "lucide-react";
import { listRecent, getFeaturedToday } from "@/lib/api";

const SECTION_ICON = { global: Globe2, local: Building2, technology: Cpu };

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const secs = Math.floor((Date.now() - then) / 1000);
  if (secs < 60) return "just now";
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function CapsuleCard({ capsule, onClick, testId, accent = false }) {
  const headlines = capsule.headlines || [];
  const loc = capsule.location
    ? [capsule.location.city, capsule.location.region, capsule.location.country].filter(Boolean).join(", ")
    : null;
  const Icon = capsule.mode === "audio" ? Mic2 : FileText;

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={`group flex flex-col gap-4 border p-5 text-left transition-all hover:-translate-y-0.5 hover:border-zinc-950 ${
        accent ? "border-zinc-950 bg-zinc-950 text-white" : "border-zinc-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between">
        <div className={`flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] ${accent ? "text-rose-400" : "text-rose-600"}`}>
          <Icon className="h-3 w-3" />
          {capsule.duration_minutes}m · {capsule.mode || "text"}
        </div>
        <span className={`font-mono text-[10px] uppercase tracking-[0.25em] ${accent ? "text-zinc-300" : "text-zinc-400"}`}>
          {timeAgo(capsule.created_at)}
        </span>
      </div>
      <div className="min-h-[68px]">
        {headlines.length > 0 ? (
          <p className="font-display text-xl font-bold leading-tight line-clamp-3">
            {headlines[0]}
          </p>
        ) : (
          <p className={`text-sm italic ${accent ? "text-zinc-300" : "text-zinc-400"}`}>Brewing...</p>
        )}
      </div>
      {loc && (
        <p className={`font-mono text-[10px] uppercase tracking-[0.25em] ${accent ? "text-zinc-400" : "text-zinc-500"}`}>{loc}</p>
      )}
      {headlines.length > 1 && (
        <ul className={`space-y-1.5 text-xs leading-snug ${accent ? "text-zinc-300" : "text-zinc-600"}`}>
          {headlines.slice(1, 3).map((h, i) => (
            <li key={`${capsule.id}-h-${i}`} className="line-clamp-2">• {h}</li>
          ))}
        </ul>
      )}
      <div className={`mt-auto flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.3em] transition-colors ${accent ? "text-white" : "text-zinc-950"}`}>
        <Play className="h-3 w-3 fill-current" />
        Open capsule
      </div>
    </button>
  );
}

export default function RecentCapsules() {
  const navigate = useNavigate();
  const [featured, setFeatured] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [f, r] = await Promise.allSettled([getFeaturedToday(), listRecent(6)]);
        if (!alive) return;
        if (f.status === "fulfilled") setFeatured(f.value);
        if (r.status === "fulfilled") setRecent(r.value.filter((c) => !c.featured));
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    // Also poll every 4s if featured is still brewing, so its headline appears.
    const iv = setInterval(async () => {
      if (!alive) return;
      try {
        const f = await getFeaturedToday();
        if (alive) setFeatured(f);
        if (f && f.status === "ready") clearInterval(iv);
      } catch (err) {
        console.debug("featured poll failed, will retry:", err?.message || err);
      }
    }, 4000);
    return () => { alive = false; clearInterval(iv); };
  }, []);

  const featuredHeadlines = featured?.segments?.map((s) => s.headline).slice(0, 5) || [];
  const featuredCard = featured
    ? {
        id: featured.id,
        created_at: featured.created_at,
        duration_minutes: featured.duration_minutes,
        mode: featured.mode,
        location: featured.location,
        headlines: featuredHeadlines,
      }
    : null;

  if (loading && !featuredCard && recent.length === 0) return null;
  if (!featuredCard && recent.length === 0) return null;

  return (
    <section className="mt-20 border-t border-zinc-950 pt-14">
      <div className="mb-6 flex items-baseline justify-between">
        <div>
          <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-rose-600">
            <Sparkles className="mr-1.5 inline h-3 w-3" />
            Today's brew · replay recent
          </p>
          <h2 className="mt-3 font-display text-3xl font-black tracking-tight text-zinc-950 md:text-4xl">
            Skip the wait — jump into a ready capsule
          </h2>
        </div>
        <p className="hidden font-mono text-[11px] uppercase tracking-[0.3em] text-zinc-500 md:block">
          <Clock className="mr-1 inline h-3 w-3" /> Auto-refreshed daily
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        {featuredCard && (
          <CapsuleCard
            capsule={featuredCard}
            testId="featured-capsule"
            accent
            onClick={() => navigate(`/capsule/${featuredCard.id}`)}
          />
        )}
        {recent.slice(0, featuredCard ? 5 : 6).map((c) => (
          <CapsuleCard
            key={c.id}
            capsule={c}
            testId={`recent-capsule-${c.id}`}
            onClick={() => navigate(`/capsule/${c.id}`)}
          />
        ))}
      </div>
    </section>
  );
}
